import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Eye, Link2, Loader2, LogIn, Pencil } from 'lucide-react';
import { api, API } from '../lib/api';
import { DOCS_URL, entrarYVolver } from '../lib/dominios';
import {
  INICIO, TEMA_DEFECTO, cargarFuentes, erroresDe, esPregunta, fondoCabecera, recorrido, secciones,
  siguienteSeccion, varsTema,
} from '../lib/formularios';
import { useStore } from '../store/useStore';
import { ProductIcon, OFFICE_APPS } from '../components/office/icons';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import TarjetaItem from '../components/forms/responder/TarjetaItem';
import { TarjetaAviso, TarjetaCorreo, TarjetaEncabezado, TarjetaSeccion } from '../components/forms/responder/Encabezados';
import { Confirmacion, DialogoEnlacePrecompletado } from '../components/forms/responder/Confirmacion';
import VistaCalificacion from '../components/forms/responder/VistaCalificacion';
import useBorrador from '../components/forms/responder/useBorrador';
import Resumen from '../components/forms/respuestas/Resumen';
import {
  RE_CORREO, enlacePrecompletado, itemsEnOrden, leerPrecompletado, mensajeDe, nuevaOperacion,
  respuestasDeRecorrido, seccionDe, semillaDeVisita,
} from '../components/forms/responder/utilidades';
import '../components/forms/forms.css';
import '../components/forms/responder/responder.css';

/*
 * Orvian Forms — la página para responder un formulario.
 *
 *   modo "publico"      → /f/:token (docs.orvian.mx, sin sesión obligatoria)
 *   modo "vista_previa" → /form/:id/preview (quien edita; no guarda nada)
 *
 * Parámetros: ?e.<item>=valor precompleta · ?editar=<t> edita una respuesta · ?calificacion=<t>
 * muestra la puntuación · ?resumen=1 el resumen de respuestas · ?embed=1 sin marco ·
 * ?precompletar=1 (vista previa) genera enlaces precompletados.
 *
 * El servidor vuelve a validar TODO al enviar; lo de aquí es para avisar antes y no hacer esperar.
 * Contrato: email/claude/formularios.md.
 */

export default function FormResponder({ modo = 'publico' }) {
  const loc = useLocation();
  // Cambiar de ?editar a ?resumen (o volver a responder) es otra pantalla: se monta de cero
  return <Responder key={`${loc.pathname}${loc.search}`} modo={modo} />;
}

/* ============================== Marco común ============================== */

function PieFormulario() {
  return (
    <footer className="fm-pie fr-pie">
      <p>Nunca envíes contraseñas a través de un formulario.</p>
      <p>Este contenido no fue creado ni aprobado por Orvian.</p>
      <p className="fr-pie-marca">
        <ProductIcon kind="form" size={18} />
        <strong>{OFFICE_APPS.form?.name || 'Orvian Forms'}</strong>
      </p>
    </footer>
  );
}

function Lienzo({ tema, embed, banner, pie = true, refScroll, children }) {
  const oscuro = useStore((s) => s.theme) === 'dark';
  const vars = useMemo(() => varsTema(tema || TEMA_DEFECTO, { oscuro }), [tema, oscuro]);
  const cabecera = fondoCabecera(tema, API);
  return (
    <div className={`fm-raiz fr-pagina fm-scroll ${embed ? 'is-embed' : ''}`} style={vars} ref={refScroll}>
      {banner}
      <div className="fm-lienzo fr-lienzo">
        <div className="fm-columna">
          {cabecera && !embed && <div className="fm-cabecera-imagen" style={{ background: cabecera }} aria-hidden="true" />}
          {children}
          {pie && !embed && <PieFormulario />}
        </div>
      </div>
    </div>
  );
}

function PaginaCargando() {
  return (
    <div className="fm-raiz fr-pagina fr-pagina-neutra" style={varsTema(TEMA_DEFECTO)}>
      <div className="fr-cargando"><Loader2 className="h-9 w-9 od-spin" aria-label="Cargando formulario" /></div>
    </div>
  );
}

function ResumenPublico({ datos, token, embed, onVolver }) {
  const [estado, setEstado] = useState({ cargando: true, resumen: null, error: null });
  useEffect(() => {
    let vivo = true;
    api.get(`/forms/publico/${token}/resumen`, { skipAuthRedirect: true })
      .then(({ data }) => vivo && setEstado({ cargando: false, resumen: data, error: null }))
      .catch((err) => vivo && setEstado({ cargando: false, resumen: null, error: mensajeDe(err, 'El resumen de respuestas no está disponible.') }));
    return () => { vivo = false; };
  }, [token]);
  return (
    <Lienzo tema={datos.tema} embed={embed}>
      <div className="fm-tarjeta fm-tarjeta-encabezado fr-encabezado">
        <h1 className="fm-titulo-form">{datos.titulo}</h1>
        {estado.resumen && (
          <p className="fr-resumen-total">{estado.resumen.total} {estado.resumen.total === 1 ? 'respuesta' : 'respuestas'}</p>
        )}
        <div className="fr-aviso-acciones">
          <button type="button" className="fm-boton-texto" onClick={onVolver}>Responder el formulario</button>
        </div>
      </div>
      {estado.cargando && <div className="fr-cargando"><Loader2 className="h-8 w-8 od-spin" aria-label="Cargando" /></div>}
      {estado.error && <TarjetaAviso><p>{estado.error}</p></TarjetaAviso>}
      {estado.resumen && <Resumen formulario={datos} resumen={estado.resumen} publico />}
    </Lienzo>
  );
}

/* ============================== Página ============================== */

function Responder({ modo }) {
  const { token: tokenRuta, id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const params = useMemo(() => new URLSearchParams(loc.search), [loc.search]);
  const vistaPrevia = modo === 'vista_previa';
  const editarToken = vistaPrevia ? null : params.get('editar');
  const calificacionToken = vistaPrevia ? null : params.get('calificacion');
  const verResumen = !vistaPrevia && params.get('resumen') === '1';
  const embed = params.get('embed') === '1';
  const precompletar = vistaPrevia && params.get('precompletar') === '1';

  const [carga, setCarga] = useState({ estado: 'cargando', datos: null, error: null });
  const datos = carga.datos;
  const borrador = useBorrador(tokenRuta, !vistaPrevia && !editarToken);

  const [respuestas, setRespuestas] = useState({});
  const [correo, setCorreo] = useState('');
  const [pedirCopia, setPedirCopia] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [actual, setActual] = useState(INICIO);
  const [errores, setErrores] = useState({});
  const [errorCorreo, setErrorCorreo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [subiendo, setSubiendo] = useState(0);
  const [resultado, setResultado] = useState(null);
  const [cerrado, setCerrado] = useState(null);
  const [yaRespondio, setYaRespondio] = useState(null);
  const [pideSesion, setPideSesion] = useState(false);
  const [trampa, setTrampa] = useState('');
  const [enlace, setEnlace] = useState(null);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [enfoque, setEnfoque] = useState(null);

  const operacion = useRef(nuevaOperacion());
  const inicio = useRef(Date.now());
  const scroll = useRef(null);
  const semilla = useMemo(() => semillaDeVisita(tokenRuta || id), [tokenRuta, id]);

  /* ---------- Carga ---------- */
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const url = vistaPrevia ? `/forms/${id}/vista-previa` : `/forms/publico/${tokenRuta}`;
        const { data } = await api.get(url, { skipAuthRedirect: !vistaPrevia });
        if (!vivo) return;
        let iniciales = leerPrecompletado(data.items, loc.search);
        let correoInicial = '';
        let hist = [];
        let seccion = INICIO;
        if (editarToken) {
          try {
            const r = await api.get(`/forms/publico/${tokenRuta}/respuesta`, { params: { editar: editarToken }, skipAuthRedirect: true });
            iniciales = r.data.respuestas || {};
            correoInicial = r.data.correo || '';
          } catch (err) {
            if (!vivo) return;
            setCarga({ estado: 'editar_invalido', datos: data, error: mensajeDe(err, 'Este enlace para editar ya no es válido.') });
            return;
          }
        } else if (!vistaPrevia && calificacionToken === null && data.ajustes?.borrador_automatico !== false) {
          const b = borrador.leer();
          if (b) {
            const ids = new Set(secciones(data.items).map((s) => s.id));
            iniciales = { ...iniciales, ...(b.respuestas || {}) };
            correoInicial = b.correo || '';
            if (b.actual && ids.has(b.actual)) {
              seccion = b.actual;
              hist = (b.historial || []).filter((s) => ids.has(s));
            }
          }
        }
        if (!correoInicial && data.ajustes?.recopilar_correos === 'escritos' && data.sesion?.correo) {
          correoInicial = data.sesion.correo;
        }
        setRespuestas(iniciales);
        setCorreo(correoInicial);
        setHistorial(hist);
        setActual(seccion);
        setCarga({ estado: 'listo', datos: data, error: null });
      } catch (err) {
        if (!vivo) return;
        const st = err.response?.status;
        setCarga({ estado: st === 404 || st === 403 ? 'no_existe' : 'error', datos: null, error: mensajeDe(err, null) });
      }
    })();
    return () => { vivo = false; };
    // Se carga una vez por montaje (la página se vuelve a montar al cambiar la URL)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!datos) return;
    document.title = `${datos.titulo || 'Formulario'} · ${OFFICE_APPS.form?.name || 'Orvian Forms'}`;
    cargarFuentes(datos.tema);
  }, [datos]);

  /* ---------- Borrador ---------- */
  const guardaBorrador = carga.estado === 'listo' && !vistaPrevia && !editarToken && !calificacionToken && !verResumen
    && !resultado && datos?.ajustes?.borrador_automatico !== false;
  useEffect(() => {
    if (!guardaBorrador) return undefined;
    const t = setTimeout(() => {
      if (!Object.keys(respuestas).length && !correo && !historial.length) borrador.borrar();
      else borrador.guardar({ respuestas, correo, historial, actual });
    }, 400);
    return () => clearTimeout(t);
  }, [guardaBorrador, respuestas, correo, historial, actual, borrador]);

  /* ---------- Desplazamiento ---------- */
  const arriba = () => {
    try {
      scroll.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      /* navegador sin scroll suave */
    }
  };
  useEffect(() => {
    if (!enfoque) return undefined;
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(`pregunta-${enfoque}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const campo = el.querySelector('input:not([type=hidden]):not([disabled]), textarea, select, button[role=radio]');
        campo?.focus({ preventScroll: true });
      }
      setEnfoque(null);
    });
    return () => cancelAnimationFrame(raf);
  }, [enfoque, actual]);

  /* ---------- Derivados ---------- */
  const lista = useMemo(() => (datos ? secciones(datos.items) : []), [datos]);
  const indice = Math.max(0, lista.findIndex((s) => s.id === actual));
  const seccion = lista[indice];
  const itemsSeccion = useMemo(() => (seccion ? itemsEnOrden(seccion, datos?.ajustes, semilla) : []), [seccion, datos, semilla]);

  const cambiar = useCallback((itemId) => (valor) => {
    setRespuestas((r) => {
      const n = { ...r };
      if (valor === null || valor === undefined) delete n[itemId];
      else n[itemId] = valor;
      return n;
    });
    setErrores((e) => {
      if (!e[itemId]) return e;
      const n = { ...e };
      delete n[itemId];
      return n;
    });
  }, []);
  const onSubiendo = useCallback((d) => setSubiendo((n) => Math.max(0, n + d)), []);

  if (carga.estado === 'cargando') return <PaginaCargando />;

  if (carga.estado === 'no_existe' || carga.estado === 'error') {
    return (
      <Lienzo tema={TEMA_DEFECTO} embed={embed}>
        <TarjetaAviso
          titulo={carga.estado === 'no_existe' ? 'Este formulario no está disponible' : 'No se pudo abrir el formulario'}
          acciones={carga.estado === 'error' ? (
            <button type="button" className="fm-boton" onClick={() => window.location.reload()}>Reintentar</button>
          ) : null}
        >
          <p>
            {carga.estado === 'no_existe'
              ? 'Este formulario no existe o ya no acepta respuestas. Si crees que es un error, pide el enlace de nuevo a quien te lo envió.'
              : (carga.error || 'Revisa tu conexión e inténtalo de nuevo.')}
          </p>
        </TarjetaAviso>
      </Lienzo>
    );
  }

  const ajustes = datos.ajustes || {};
  const irA = (url) => {
    if (!url) return;
    if (/^https?:\/\//i.test(url)) window.location.assign(url);
    else if (url.startsWith('?')) nav(`${loc.pathname}${url}`);
    else nav(url);
  };

  if (carga.estado === 'editar_invalido') {
    return (
      <Lienzo tema={datos.tema} embed={embed}>
        <TarjetaAviso
          titulo={datos.titulo}
          acciones={<button type="button" className="fm-boton" onClick={() => nav(loc.pathname)}>Responder de nuevo</button>}
        >
          <p>{carga.error}</p>
        </TarjetaAviso>
      </Lienzo>
    );
  }

  if (calificacionToken) {
    return (
      <Lienzo tema={datos.tema} embed={embed}>
        <VistaCalificacion datos={datos} token={tokenRuta} editarToken={calificacionToken} />
      </Lienzo>
    );
  }

  if (verResumen) {
    return <ResumenPublico datos={datos} token={tokenRuta} embed={embed} onVolver={() => nav(loc.pathname)} />;
  }

  /* ---------- Puertas: sesión, cerrado, ya respondió ---------- */
  if (!vistaPrevia && ((datos.sesion?.requerida && !datos.sesion?.correo) || pideSesion)) {
    const motivo = ajustes.recopilar_correos === 'verificados'
      ? 'Este formulario registra el correo de tu cuenta. Inicia sesión para responder.'
      : ajustes.limitar_una_respuesta
        ? 'Este formulario solo admite una respuesta por persona. Inicia sesión para responder.'
        : 'Solo pueden responder personas con una cuenta autorizada. Inicia sesión para continuar.';
    return (
      <Lienzo tema={datos.tema} embed={embed}>
        <TarjetaAviso
          titulo={datos.titulo}
          acciones={(
            <button type="button" className="fm-boton" onClick={() => entrarYVolver()}>
              <LogIn className="h-4 w-4" /> Iniciar sesión
            </button>
          )}
        >
          <p>{motivo}</p>
        </TarjetaAviso>
      </Lienzo>
    );
  }

  if (!vistaPrevia && !resultado && (cerrado || datos.estado?.acepta === false)) {
    return (
      <Lienzo tema={datos.tema} embed={embed}>
        <TarjetaAviso titulo={datos.titulo}>
          <p className="fr-aviso-cerrado">
            {cerrado || datos.estado?.mensaje_cerrado || `El formulario «${datos.titulo || 'sin título'}» ya no acepta respuestas.`}
          </p>
          <p className="fm-texto-2">Si crees que es un error, comunícate con quien lo creó.</p>
        </TarjetaAviso>
      </Lienzo>
    );
  }

  const ya = yaRespondio || (!vistaPrevia && !editarToken && datos.ya_respondio);
  if (ya && !resultado) {
    return (
      <Lienzo tema={datos.tema} embed={embed}>
        <TarjetaAviso
          titulo={datos.titulo}
          acciones={ya.editar_url ? (
            <button type="button" className="fm-boton" onClick={() => irA(ya.editar_url)}>
              <Pencil className="h-4 w-4" /> Editar tu respuesta
            </button>
          ) : null}
        >
          <p>Ya respondiste este formulario.</p>
          <p className="fm-texto-2">Solo se permite una respuesta por persona.</p>
        </TarjetaAviso>
      </Lienzo>
    );
  }

  /* ---------- Banner de vista previa ---------- */
  const banner = vistaPrevia && !embed ? (
    <div className="fr-banner" role="region" aria-label="Vista previa">
      <div className="fr-banner-texto">
        {precompletar ? <Link2 className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
        <span>{precompletar ? 'Enlace precompletado · responde para generar el enlace' : 'Vista previa · las respuestas no se guardan'}</span>
      </div>
      <div className="fr-banner-acciones">
        {precompletar && (
          <button type="button" className="od-btn-primary" onClick={() => setEnlace(enlacePrecompletado(DOCS_URL || window.location.origin, datos.token, datos.items, respuestas))}>
            <Link2 className="h-4 w-4" /> Obtener enlace
          </button>
        )}
        <button type="button" className="od-btn-secondary" onClick={() => nav(`/form/${id}`)}>
          <Pencil className="h-4 w-4" /> Editar formulario
        </button>
      </div>
    </div>
  ) : null;

  /* ---------- Confirmación ---------- */
  const reiniciar = () => {
    borrador.borrar();
    operacion.current = nuevaOperacion();
    inicio.current = Date.now();
    setRespuestas({});
    setCorreo(ajustes.recopilar_correos === 'escritos' && datos.sesion?.correo ? datos.sesion.correo : '');
    setPedirCopia(false);
    setHistorial([]);
    setActual(INICIO);
    setErrores({});
    setErrorCorreo(null);
    setResultado(null);
    arriba();
  };

  if (resultado) {
    return (
      <Lienzo tema={datos.tema} embed={embed} banner={banner} refScroll={scroll}>
        <Confirmacion
          titulo={datos.titulo}
          resultado={resultado}
          ajustes={ajustes}
          vistaPrevia={vistaPrevia}
          onOtra={() => (editarToken ? nav(loc.pathname) : reiniciar())}
          onIr={irA}
        />
      </Lienzo>
    );
  }

  /* ---------- Navegación entre secciones ---------- */
  const pideCorreo = ajustes.recopilar_correos === 'escritos';
  const destino = siguienteSeccion(datos, seccion.id, respuestas);
  const esUltima = destino === 'enviar';
  const hayObligatorias = (datos.items || []).some((it) => esPregunta(it) && it.obligatoria) || pideCorreo;

  const validar = () => {
    const errs = erroresDe(itemsSeccion, respuestas);
    let errC = null;
    if (indice === 0 && pideCorreo) {
      if (!correo.trim()) errC = 'Esta pregunta es obligatoria';
      else if (!RE_CORREO.test(correo.trim())) errC = 'Introduce un correo electrónico válido';
    }
    setErrores(errs);
    setErrorCorreo(errC);
    const primero = errC ? '__correo__' : itemsSeccion.find((it) => errs[it.id])?.id;
    if (primero) {
      setEnfoque(primero);
      return false;
    }
    return true;
  };

  const siguiente = () => {
    if (subiendo > 0) return;
    if (!validar()) return;
    setHistorial((h) => [...h, seccion.id]);
    setActual(destino);
    arriba();
  };

  const atras = () => {
    if (!historial.length) return;
    const previo = historial[historial.length - 1];
    setHistorial(historial.slice(0, -1));
    setActual(previo);
    setErrores({});
    arriba();
  };

  const aplicarErroresServidor = (errs) => {
    const { correo: eCorreo, __correo__: eCorreo2, ...resto } = errs || {};
    setErrores(resto);
    setErrorCorreo(eCorreo || eCorreo2 || null);
    const primero = eCorreo || eCorreo2 ? '__correo__' : (datos.items || []).find((it) => resto[it.id])?.id;
    if (!primero) return;
    const sec = primero === '__correo__' ? INICIO : seccionDe(datos.items, primero);
    const ruta = recorrido(datos, respuestas);
    const i = ruta.indexOf(sec);
    setHistorial(i > 0 ? ruta.slice(0, i) : []);
    setActual(sec);
    setEnfoque(primero);
  };

  const enviar = async () => {
    if (enviando || subiendo > 0) return;
    if (!validar()) return;
    const visitadas = recorrido(datos, respuestas);
    if (vistaPrevia) {
      setResultado({ mensaje: ajustes.mensaje_confirmacion || 'Se registró tu respuesta.' });
      arriba();
      return;
    }
    const cuerpo = {
      operacion_id: operacion.current,
      correo: pideCorreo ? correo.trim() : null,
      pedir_copia: pedirCopia,
      respuestas: respuestasDeRecorrido(datos.items, respuestas, visitadas),
      trampa,
      ms: Date.now() - inicio.current,
    };
    if (editarToken) cuerpo.editar = editarToken;
    setEnviando(true);
    try {
      const { data } = await api.post(`/forms/publico/${tokenRuta}/respuestas`, cuerpo, { skipAuthRedirect: true });
      borrador.borrar();
      setResultado(data || {});
      arriba();
    } catch (err) {
      const st = err.response?.status;
      const d = err.response?.data?.detail;
      if (st === 422 && d && typeof d === 'object' && d.errores) {
        aplicarErroresServidor(d.errores);
        toast.error(d.mensaje || 'Revisa las preguntas marcadas');
      } else if (st === 403) {
        setCerrado(mensajeDe(err, 'Este formulario ya no acepta respuestas.'));
      } else if (st === 409) {
        setYaRespondio({ editar_url: (d && typeof d === 'object' && d.editar_url) || null });
      } else if (st === 401) {
        setPideSesion(true);
      } else if (st === 429) {
        toast.error('Demasiados envíos seguidos. Espera un momento e inténtalo de nuevo.');
      } else {
        // El mismo operacion_id se reintenta: si el envío sí llegó, el servidor no lo duplica
        toast.error(mensajeDe(err, 'No se pudo enviar. Revisa tu conexión e inténtalo de nuevo.'));
      }
    } finally {
      setEnviando(false);
    }
  };

  const borrarTodo = () => {
    setConfirmarBorrar(false);
    borrador.borrar();
    setRespuestas({});
    if (!(pideCorreo && datos.sesion?.correo)) setCorreo('');
    setPedirCopia(false);
    setHistorial([]);
    setActual(INICIO);
    setErrores({});
    setErrorCorreo(null);
    arriba();
  };

  const ruta = recorrido(datos, respuestas);
  const posicion = Math.max(0, ruta.indexOf(seccion.id));
  const totalPaginas = Math.max(ruta.length, posicion + 1);
  const conCorreoParaCopia = pideCorreo || ajustes.recopilar_correos === 'verificados';

  return (
    <Lienzo tema={datos.tema} embed={embed} banner={banner} refScroll={scroll}>
      <TarjetaEncabezado datos={datos} completa={indice === 0} hayObligatorias={hayObligatorias} />
      {indice > 0 && <TarjetaSeccion seccion={seccion} indice={indice} total={lista.length} />}
      {indice === 0 && pideCorreo && (
        <TarjetaCorreo
          correo={correo}
          onCorreo={(v) => {
            setCorreo(v);
            if (errorCorreo) setErrorCorreo(null);
          }}
          error={errorCorreo}
        />
      )}

      {itemsSeccion.map((item) => (
        <TarjetaItem
          key={item.id}
          item={item}
          valor={respuestas[item.id]}
          onCambio={cambiar(item.id)}
          error={errores[item.id]}
          semilla={semilla}
          token={tokenRuta}
          vistaPrevia={vistaPrevia}
          onSubiendo={onSubiendo}
          mostrarPuntos={!!ajustes.es_cuestionario}
        />
      ))}

      {esUltima && ajustes.enviar_copia === 'si_lo_pide' && conCorreoParaCopia && (
        <label className="fr-copia">
          <input type="checkbox" checked={pedirCopia} onChange={(e) => setPedirCopia(e.target.checked)} />
          <span>Enviarme una copia de mis respuestas</span>
        </label>
      )}
      {esUltima && ajustes.enviar_copia === 'siempre' && conCorreoParaCopia && (
        <p className="fr-copia-nota">Se enviará una copia de tus respuestas al correo que indicaste.</p>
      )}

      {/* Campo trampa: invisible para personas; los robots que rellenan todo lo delatan */}
      <div className="fr-trampa" aria-hidden="true">
        <label>
          Sitio web
          <input name="sitio_web" tabIndex={-1} autoComplete="off" value={trampa} onChange={(e) => setTrampa(e.target.value)} />
        </label>
      </div>

      <div className="fr-acciones">
        <div className="fr-acciones-nav">
          {historial.length > 0 && (
            <button type="button" className="fm-boton-borde" onClick={atras} disabled={enviando}>
              <ArrowLeft className="h-4 w-4" /> Atrás
            </button>
          )}
          {esUltima ? (
            precompletar ? (
              <button type="button" className="fm-boton" onClick={() => setEnlace(enlacePrecompletado(DOCS_URL || window.location.origin, datos.token, datos.items, respuestas))}>
                <Link2 className="h-4 w-4" /> Obtener enlace
              </button>
            ) : (
              <button type="button" className="fm-boton" onClick={enviar} disabled={enviando || subiendo > 0} aria-busy={enviando}>
                {enviando && <Loader2 className="h-4 w-4 od-spin" aria-hidden="true" />}
                {subiendo > 0 ? 'Subiendo archivos…' : 'Enviar'}
              </button>
            )
          ) : (
            <button type="button" className="fm-boton" onClick={siguiente} disabled={subiendo > 0}>
              {subiendo > 0 ? 'Subiendo archivos…' : 'Siguiente'}
            </button>
          )}
          {ajustes.barra_progreso && lista.length > 1 && (
            <div className="fm-progreso fr-progreso" aria-label={`Página ${posicion + 1} de ${totalPaginas}`}>
              <div className="fm-progreso-barra" role="progressbar" aria-valuemin={0} aria-valuemax={totalPaginas} aria-valuenow={posicion + 1}>
                <span style={{ width: `${Math.round(((posicion + 1) / totalPaginas) * 100)}%` }} />
              </div>
              <span>Página {posicion + 1} de {totalPaginas}</span>
            </div>
          )}
        </div>
        <button type="button" className="fm-boton-texto fr-borrar" onClick={() => setConfirmarBorrar(true)} disabled={enviando}>
          Borrar formulario
        </button>
      </div>

      <DialogoEnlacePrecompletado
        abierto={enlace !== null}
        onCerrar={() => setEnlace(null)}
        enlace={enlace || ''}
        sinToken={!datos.token}
      />

      <Dialog open={confirmarBorrar} onOpenChange={setConfirmarBorrar}>
        <DialogContent className="sm:max-w-[420px] fr-dialogo">
          <DialogHeader>
            <DialogTitle>¿Borrar el formulario?</DialogTitle>
            <DialogDescription>Se quitarán tus respuestas de todas las preguntas. No se puede deshacer.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmarBorrar(false)}>Cancelar</Button>
            <Button onClick={borrarTodo}>Borrar formulario</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Lienzo>
  );
}
